/**
 * Batch Genealogy — API layer (live only).
 *
 * Every function POSTs to njm-corda-pcmp
 *   `${getApiBaseUrl(appUrl)}/batchGenealogy/<op>`
 * which assembles the response from the ledger-replicated tables (enriched via
 * njm-sml). There is NO bundled/dummy data and NO hardcoded stage logic on the
 * frontend: all lineage, stages and ordering come from the middleware. On a
 * missing base URL or a failed/empty backend call, each function returns an
 * empty envelope in the same shape so the components render an empty state
 * rather than throwing.
 *
 * Response envelopes are the platform standard { status, data } and match what
 * the components consume, so no component depends on any batch-genealogy
 * constant.
 */
import axios from 'axios';
import * as XLSX from 'xlsx-js-style';
import { appUrl } from '../../../../../../utils/config';
import { getApiBaseUrl, formatDisplayDate } from '../../CommonMethods/CommonMethods';
import { STATUS } from './statusConfig';

// Endpoint namespace on the middleware (Router.post('/batchGenealogy/<op>')).
const NS = 'batchGenealogy';

const post = (base, op, payload) => axios.post(`${base}/${NS}/${op}`, payload || {});

// ── filters ──────────────────────────────────────────────────────────────────
// POST /batchGenealogy/filters  {}
export const fetchGenealogyFilters = async () => {
  const base = getApiBaseUrl(appUrl);
  if (!base) return { status: false, data: { projects: [] } };
  try {
    const res = await post(base, 'filters', {});
    const data = res && res.data && res.data.data;
    if (res && res.data && res.data.status && data && Array.isArray(data.projects)) {
      return { status: true, data: { projects: data.projects } };
    }
    return { status: false, data: { projects: [] } };
  } catch (err) {
    console.error('[BatchGenealogy] filters failed:', err);
    return { status: false, data: { projects: [] } };
  }
};

// POST /batchGenealogy/campaigns  { projectId }
export const fetchCampaigns = async ({ projectId } = {}) => {
  const base = getApiBaseUrl(appUrl);
  if (!base) return { status: false, data: [] };
  try {
    const res = await post(base, 'campaigns', { projectId });
    const data = res && res.data && res.data.data;
    if (res && res.data && res.data.status && Array.isArray(data)) {
      return { status: true, data };
    }
    return { status: false, data: [] };
  } catch (err) {
    console.error('[BatchGenealogy] campaigns failed:', err);
    return { status: false, data: [] };
  }
};

// POST /batchGenealogy/batches  { projectId, campaignId }
export const fetchBatchNumbers = async ({ projectId, campaignId } = {}) => {
  const base = getApiBaseUrl(appUrl);
  if (!base) return { status: false, data: [] };
  try {
    const res = await post(base, 'batches', { projectId, campaignId });
    const data = res && res.data && res.data.data;
    if (res && res.data && res.data.status && Array.isArray(data)) {
      return { status: true, data };
    }
    return { status: false, data: [] };
  } catch (err) {
    console.error('[BatchGenealogy] batches failed:', err);
    return { status: false, data: [] };
  }
};

// ── genealogy report (ribbon + lineage graph/table) ───────────────────────────
// POST /batchGenealogy/report  { projectId, campaignId, batchNumber }
// Date fields the middleware returns as raw ISO timestamps, formatted here for
// display. Middleware must send the full instant (not a pre-truncated
// "YYYY-MM-DD"), otherwise the UTC calendar date is already locked in upstream
// and no amount of formatting here can recover the correct local day.
const DATE_FIELDS = ['mfgDate', 'retestDate'];

const fmtDateFields = (obj) => {
  if (!obj) return obj;
  const out = { ...obj };
  DATE_FIELDS.forEach((k) => {
    if (k in out) out[k] = formatDisplayDate(out[k]);
  });
  return out;
};

const withFormattedDates = (data) => ({
  ...data,
  nodes: Array.isArray(data.nodes) ? data.nodes.map(fmtDateFields) : data.nodes,
});

export const fetchBatchGenealogyReport = async ({ projectId, campaignId, batchNumber } = {}) => {
  const base = getApiBaseUrl(appUrl);
  if (!base) return { status: false, data: null };
  try {
    const res = await post(base, 'report', { projectId, campaignId, batchNumber });
    const data = res && res.data && res.data.data;
    if (res && res.data && res.data.status && data && Array.isArray(data.nodes)) {
      // Dates are formatted once here, on ingest — the same place and the same
      // helper the Yield tab uses (tabDataMapper). Every downstream consumer
      // (Lineage table, lineage graph, CSV + PNG exports) then renders the
      // already-formatted string, so they cannot drift apart.
      return { status: true, data: withFormattedDates(data) };
    }
    return { status: false, data: null };
  } catch (err) {
    console.error('[BatchGenealogy] report failed:', err);
    return { status: false, data: null };
  }
};

// ── single batch detail (side panel) ──────────────────────────────────────────
// POST /batchGenealogy/batchDetail  { batchNumber }
export const fetchBatchDetail = async (batchId) => {
  const base = getApiBaseUrl(appUrl);
  if (!base) return { status: false, data: null };
  try {
    const res = await post(base, 'batchDetail', { batchNumber: batchId });
    const data = res && res.data && res.data.data;
    if (res && res.data && res.data.status && data) {
      // Same ingest-time formatting as the report (mfgDate / retestDate).
      return { status: true, data: fmtDateFields(data) };
    }
    return { status: false, data: null };
  } catch (err) {
    console.error('[BatchGenealogy] batchDetail failed:', err);
    return { status: false, data: null };
  }
};

// ── campaign-wide deviations & failures (All deviations panel) ─────────────────
// POST /batchGenealogy/campaignDeviations  { campaignId, lineageBatchIds }
export const fetchCampaignDeviations = async ({ campaignId, lineageBatchIds } = {}) => {
  const base = getApiBaseUrl(appUrl);
  if (!base) return { status: false, data: { campaignId, deviations: [] } };
  try {
    const res = await post(base, 'campaignDeviations', { campaignId, lineageBatchIds });
    const data = res && res.data && res.data.data;
    if (res && res.data && res.data.status && data && Array.isArray(data.deviations)) {
      return { status: true, data };
    }
    return { status: false, data: { campaignId, deviations: [] } };
  } catch (err) {
    console.error('[BatchGenealogy] campaignDeviations failed:', err);
    return { status: false, data: { campaignId, deviations: [] } };
  }
};

// ── Export to QA ──────────────────────────────────────────────────────────────
// QA needs BOTH artefacts of the selected batch's lineage:
//   • Batch Genealogy.<batch>.png  — the Lineage graph (chart)
//   • Batch Genealogy.<batch>.csv  — the Lineage table
// They download as two separate files from a single click (no zip). The chart is
// rendered client-side as a self-contained SVG (independent of the current
// graph/table view) and rasterised to PNG; the table is written as CSV, matching
// the "Export for QA (CSV)" button label. index.jsx calls both and falls back to
// the table only if the chart render fails; these helpers each produce one file.
// exportLineageXLS still writes the same table as a styled .xlsx workbook and is
// kept for callers that want Excel formatting rather than plain CSV.

// Shared row builder — the FULL displayed lineage, mirroring the on-screen Lineage
// table 1:1: the same six columns, the same step→id merge in the Produced Batch
// cell, the same parents join, yield "%", status label and MFG-date dash fallback.
// Row order follows the API-provided `steps` (final→first), the exact sort the
// Lineage table uses; no hardcoded stage order. Nothing is filtered or truncated.
const LINEAGE_COLS = ['Produced Batch', 'Consumed From (Parents)', 'Yield', 'Qty', 'Status', 'MFG Date'];

const buildLineageRows = (nodes, steps = []) => {
  const rank = {};
  (steps || []).forEach((s, i) => { rank[s.id] = i; });
  const rankOf = (step) => (rank[step] != null ? rank[step] : Number.MAX_SAFE_INTEGER);
  const statusLabelOf = (k) => (STATUS[k] && STATUS[k].label) || k || '';
  return [...nodes]
    .sort((a, b) => rankOf(a.step) - rankOf(b.step) || String(a.id).localeCompare(String(b.id)))
    .map((n) => [
      // Produced Batch cell = stage badge + id, as the table renders it.
      `${n.step} ${n.id}`,
      (n.parents && n.parents.length ? n.parents.join(', ') : '— (raw materials)'),
      `${n.yield}%`,
      n.qty,
      statusLabelOf(n.status),
      n.mfgDate || '—',
    ]);
};

// Metadata preamble shared by the CSV and XLSX writers, so both carry the same
// batch context above the table.
const buildLineageMeta = (header) => [
  ['Batch Genealogy — Export to QA'],
  ['Final Product', header.finalBatch],
  ['Product', header.productName],
  ['CMO / Site', header.cmoSite],
  ['Project', header.project],
  ['Campaign', header.campaign],
  ['P.O.', header.po],
  ['Qty Produced', header.qtyProduced],
  [],
];

// CSV of the Lineage table → `Batch Genealogy.<batch>.csv`.
// RFC 4180 quoting: a field is quoted when it contains a comma, double quote or
// newline, and embedded double quotes are doubled. Values here legitimately
// contain commas (the parents list is joined with ", ") so this is required, not
// defensive. Written with a UTF-8 BOM because the rows use em dashes ("—") as
// the empty-value fallback and Excel misreads UTF-8 without it.
export const exportLineageCSV = (header, nodes, steps = []) => {
  const csvCell = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const aoa = [...buildLineageMeta(header), LINEAGE_COLS, ...buildLineageRows(nodes, steps)];
  const csv = aoa.map((row) => (row || []).map(csvCell).join(',')).join('\r\n');

  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Batch Genealogy.${header.finalBatch}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Excel of the Lineage table → `Batch Genealogy.<batch>.xlsx`.
export const exportLineageXLS = (header, nodes, steps = []) => {
  const meta = buildLineageMeta(header);
  const rows = buildLineageRows(nodes, steps);
  const aoa = [...meta, LINEAGE_COLS, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const colWch = [22, 34, 10, 12, 16, 14];
  ws['!cols'] = colWch.map((wch) => ({ wch }));

  const headerRow = meta.length; // 0-based row index of LINEAGE_COLS
  const wrap = { wrapText: true, vertical: 'top' };

  // Every cell wraps its text (so long parent lists / values stay fully visible
  // within the fixed column widths instead of being clipped).
  for (let r = 0; r < aoa.length; r += 1) {
    for (let c = 0; c < LINEAGE_COLS.length; c += 1) {
      const ref = XLSX.utils.encode_cell({ r, c });
      const cell = ws[ref];
      if (cell) cell.s = { ...(cell.s || {}), alignment: { ...((cell.s && cell.s.alignment) || {}), ...wrap } };
    }
  }

  // Explicitly height each row to fit its wrapped content. Excel does not reliably
  // auto-fit row height for wrapped cells created programmatically, so without this
  // the wrap is set but the text still shows on one clipped line until the user
  // auto-fits. ~15pt per estimated wrapped line, capped so a very long value can't
  // create a giant row.
  ws['!rows'] = aoa.map((row) => {
    let lines = 1;
    for (let c = 0; c < LINEAGE_COLS.length; c += 1) {
      const text = row[c] == null ? '' : String(row[c]);
      if (!text) continue;
      const budget = Math.max(6, colWch[c] - 1); // chars that fit per line in this column
      const rowLines = text.split('\n').reduce((sum, seg) => sum + Math.max(1, Math.ceil(seg.length / budget)), 0);
      lines = Math.max(lines, rowLines);
    }
    return { hpt: Math.min(120, 15 * lines + 3) };
  });

  // Bold the title; give the column-header row the table's amber styling.
  const titleRef = XLSX.utils.encode_cell({ r: 0, c: 0 });
  if (ws[titleRef]) ws[titleRef].s = { ...(ws[titleRef].s || {}), font: { bold: true, sz: 13 } };
  LINEAGE_COLS.forEach((_, c) => {
    const ref = XLSX.utils.encode_cell({ r: headerRow, c });
    if (ws[ref]) {
      ws[ref].s = {
        ...(ws[ref].s || {}),
        font: { bold: true, color: { rgb: 'B45309' } },
        fill: { fgColor: { rgb: 'FFF7ED' } },
        alignment: { ...wrap },
      };
    }
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Lineage');
  XLSX.writeFile(wb, `Batch Genealogy.${header.finalBatch}.xlsx`);
};

// PNG of the Lineage graph → `Batch Genealogy.<batch>.png`.
//
// Rather than screenshotting the live React Flow canvas (html2canvas rasterises
// React Flow's SVG edge layer unreliably — the parent→product connectors drop out
// or land in the wrong place), we render the chart deterministically as a single
// self-contained SVG from the same node/step data and the SAME layout maths the
// on-screen LineageGraph uses (CARD_W / ROW_H / COL_W, centre axis at x=0, stage
// order = API steps then any extras final→first). Native SVG shapes/text only (no
// <foreignObject>, so the canvas is never tainted and toBlob always succeeds), and
// the edges are drawn by us, so they always come out correctly.
const G = { CARD_W: 250, CARD_H: 140, ROW_H: 220, COL_W: 300, PAD: 40, P: 14 };

const xmlEsc = (v) => String(v == null ? '' : v)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const clip = (str, max) => {
  const s = String(str == null ? '' : str);
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

const wrapText = (str, max) => {
  const words = String(str == null ? '' : str).split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = '';
  const flush = () => { if (cur) { lines.push(cur); cur = ''; } };
  words.forEach((word) => {
    let w = word;
    // Hard-break tokens longer than the line budget — long chemical names have
    // few spaces, so without this a single line would run wider than the reserved
    // label column and get clipped.
    while (w.length > max) { flush(); lines.push(w.slice(0, max)); w = w.slice(max); }
    if (!w) return;
    if (cur && `${cur} ${w}`.length > max) { lines.push(cur); cur = w; } else { cur = cur ? `${cur} ${w}` : w; }
  });
  flush();
  return lines.length ? lines : [''];
};

const compareStageDesc = (a, b) => String(b || '').localeCompare(String(a || ''));

// Rough per-char width upper bounds (px) so the left label column can be sized to
// hold its WIDEST wrapped line — the product descriptions are then shown in full,
// never clipped at the image edge.
const DESC_WRAP = 26;      // chars per description line
const CHAR_W_DESC = 6.4;   // font 11
const CHAR_W_TITLE = 7.8;  // font 13 bold
const LABEL_GAP = 28;      // gap between the rail text and the first card column

// Build the SVG string for the whole lineage graph.
const buildLineageSVG = (nodes, steps = []) => {
  const byStep = {};
  nodes.forEach((n) => { (byStep[n.step] = byStep[n.step] || []).push(n); });
  const stepMeta = new Map();
  (steps || []).forEach((s) => stepMeta.set(s.id, s));
  const fromSteps = (steps || []).map((s) => s.id).filter((id) => byStep[id]);
  const extra = Object.keys(byStep).filter((id) => id && !fromSteps.includes(id)).sort(compareStageDesc);
  const order = [...fromSteps, ...extra];
  const rowOf = {};
  order.forEach((id, i) => { rowOf[id] = i; });

  // Pre-wrap every stage label/description and size the left column to the widest
  // line so long product descriptions are rendered IN FULL.
  let labelColW = 0;
  const railRows = order.map((id, i) => {
    const s = stepMeta.get(id) || { label: `Step ${id}`, desc: '' };
    const descLines = wrapText(s.desc, DESC_WRAP);
    const titlePx = String(s.label || '').length * CHAR_W_TITLE;
    const descPx = descLines.reduce((m, ln) => Math.max(m, ln.length * CHAR_W_DESC), 0);
    labelColW = Math.max(labelColW, titlePx, descPx);
    return { i, label: s.label, descLines };
  });
  labelColW = Math.ceil(labelColW) + 8;

  // Top-left position of every card, centred on the x = 0 axis per stage row.
  const pos = {};
  order.forEach((stepId) => {
    const list = byStep[stepId] || [];
    const c = list.length;
    list.forEach((n, i) => { pos[n.id] = { x: (i - (c - 1) / 2) * G.COL_W, y: rowOf[stepId] * G.ROW_H }; });
  });

  // Content bounds → viewBox (reserve the measured label column on the left).
  const xsL = Object.values(pos).map((p) => p.x);
  const xsR = xsL.map((x) => x + G.CARD_W);
  const minX = Math.min(...xsL, 0);
  const maxX = Math.max(...xsR, G.CARD_W);
  const maxRow = order.length ? order.length - 1 : 0;
  const maxY = maxRow * G.ROW_H + G.CARD_H;
  const labelAnchorX = minX - LABEL_GAP;          // right edge of the rail text
  const vbX = labelAnchorX - labelColW - G.PAD;   // full label column + margin
  const vbY = -G.PAD;
  const W = (maxX + G.PAD) - vbX;
  const H = maxY + G.PAD * 2;

  const parts = [];
  parts.push(`<rect x="${vbX}" y="${vbY}" width="${W}" height="${H}" fill="#fbfcfe"/>`);

  // Edges: parent (bottom stage) top → child (upper stage) bottom, arrow at child.
  nodes.forEach((n) => {
    (n.parents || []).forEach((pid) => {
      const pp = pos[pid];
      const cp = pos[n.id];
      if (!pp || !cp) return;
      const x1 = pp.x + G.CARD_W / 2;
      const y1 = pp.y;                 // parent top edge
      const x2 = cp.x + G.CARD_W / 2;
      const y2 = cp.y + G.CARD_H;      // child bottom edge
      parts.push(`<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#b6becb" stroke-width="1.5" marker-end="url(#bgArrow)"/>`);
    });
  });

  // Stage rail (right-aligned) on the far left — full label + wrapped description.
  railRows.forEach(({ i, label, descLines }) => {
    const y = i * G.ROW_H + 8;
    parts.push(`<text x="${labelAnchorX}" y="${y + 12}" text-anchor="end" font-size="14" font-weight="700" fill="#000000">${xmlEsc(label)}</text>`);
    descLines.forEach((ln, li) => {
      parts.push(`<text x="${labelAnchorX}" y="${y + 30 + li * 15}" text-anchor="end" font-size="12" fill="#333333">${xmlEsc(ln)}</text>`);
    });
  });

  // Batch cards.
  nodes.forEach((n) => {
    const p = pos[n.id];
    if (!p) return;
    const s = STATUS[n.status] || STATUS.released;
    const { x, y } = p;
    const consumed = n.parents && n.parents.length
      ? n.parents.join(' + ')
      : (n.keyMaterial || '— raw materials');
    const r = 12;
    parts.push(`<g>`);
    // Card = light border; status colour is a rounded top accent (matches the UI node).
    parts.push(`<rect x="${x}" y="${y}" width="${G.CARD_W}" height="${G.CARD_H}" rx="${r}" fill="#ffffff" stroke="#e5e7eb" stroke-width="1"/>`);
    parts.push(`<path d="M ${x} ${y + r} A ${r} ${r} 0 0 1 ${x + r} ${y} L ${x + G.CARD_W - r} ${y} A ${r} ${r} 0 0 1 ${x + G.CARD_W} ${y + r}" fill="none" stroke="${s.color}" stroke-width="3"/>`);
    parts.push(`<circle cx="${x + G.P + 6}" cy="${y + 24}" r="6" fill="${s.color}"/>`);
    parts.push(`<text x="${x + G.P + 20}" y="${y + 29}" font-size="16" font-weight="700" fill="#000000">${xmlEsc(n.id)}</text>`);
    if (n.dev) {
      const bw = 52;
      const bx = x + G.CARD_W - G.P - bw;
      parts.push(`<rect x="${bx}" y="${y + 15}" width="${bw}" height="17" rx="8.5" fill="#f59e0b"/>`);
      parts.push(`<text x="${bx + bw / 2}" y="${y + 27}" text-anchor="middle" font-size="10" font-weight="700" fill="#ffffff">⚑ DEV</text>`);
    }
    parts.push(`<text x="${x + G.P}" y="${y + 62}" font-size="10.5" font-weight="700" fill="#000000">YIELD</text>`);
    parts.push(`<text x="${x + G.P}" y="${y + 82}" font-size="15" font-weight="700" fill="#333333">${xmlEsc(`${n.yield}%`)}</text>`);
    parts.push(`<text x="${x + G.P + 90}" y="${y + 62}" font-size="10.5" font-weight="700" fill="#000000">QTY</text>`);
    parts.push(`<text x="${x + G.P + 90}" y="${y + 82}" font-size="15" font-weight="700" fill="#333333">${xmlEsc(String(n.qty))}</text>`);
    parts.push(`<text x="${x + G.P}" y="${y + 108}" font-size="13" font-weight="600" fill="#4338ca">${xmlEsc(clip(consumed, 32))}</text>`);
    parts.push(`<text x="${x + G.P}" y="${y + 130}" font-size="12.5" fill="#333333">${xmlEsc(`${n.materials} materials`)}</text>`);
    parts.push(`<text x="${x + G.CARD_W - G.P}" y="${y + 130}" text-anchor="end" font-size="12.5" font-weight="600" fill="#ea580c">All materials ›</text>`);
    parts.push(`</g>`);
  });

  return {
    W: Math.ceil(W),
    H: Math.ceil(H),
    svg: `<svg xmlns="http://www.w3.org/2000/svg" width="${Math.ceil(W)}" height="${Math.ceil(H)}" viewBox="${vbX} ${vbY} ${W} ${H}" font-family="Roboto, Helvetica, Arial, sans-serif">`
      + `<defs><marker id="bgArrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="userSpaceOnUse">`
      + `<path d="M0,0 L8,3 L0,6 Z" fill="#b6becb"/></marker></defs>`
      + parts.join('')
      + `</svg>`,
  };
};

export const exportLineagePNG = async (header, nodes, steps = []) => {
  if (!nodes || !nodes.length) throw new Error('No lineage nodes to render');
  const { W, H, svg } = buildLineageSVG(nodes, steps);

  const img = new Image();
  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = () => reject(new Error('Failed to rasterise lineage chart'));
    img.src = dataUrl;
  });

  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = W * scale;
  canvas.height = H * scale;
  const ctx = canvas.getContext('2d');
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
  ctx.drawImage(img, 0, 0, W, H);

  await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error('Failed to render lineage chart image')); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Batch Genealogy.${header.finalBatch}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      resolve();
    }, 'image/png');
  });
};
